import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('validation')

import { i18n } from '@/i18n'

// Remove IValidationRule import and define types directly
type ValidationResult = string | true | Promise<string | true>
type ValidationFn = (value: unknown) => ValidationResult
type ValidatorFactory = (...params: any[]) => ValidationFn

/**
 * Validate a required field
 * @param message - The message to display if the field is required
 * @returns A validator function
 */
export const required = (message = 'This field is required'): ValidationFn => {
  return (value: any): ValidationResult => {
    if (value === null || value === undefined) return message
    if (typeof value === 'string' && !value.trim()) return message
    if (Array.isArray(value) && value.length === 0) return message
    return true
  }
}

/**
 * Validate an email address
 * @param message - The message to display if the email is invalid
 * @returns A validator function
 */
export const email = (message?: string): ValidationFn =>
  (value: any): ValidationResult => {
    if (!value) return true
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(value) ? true :
      message || i18n.global.t('common.validation.email')
  }

/**
 * Validate a minimum length
 * @param length - The minimum length
 * @param message - The message to display if the value is shorter than the minimum length
 * @returns A validator function
 */
export const minLength = (min: number, message?: string): ValidationFn => {
  return (value: unknown): ValidationResult => {
    if (!value || typeof value !== 'string') return true
    return value.length >= min ? true : (message || `Must be at least ${min} characters`)
  }
}

/**
 * Validate a maximum length
 * @param length - The maximum length
 * @param message - The message to display if the value is longer than the maximum length
 * @returns A validator function
 */
export const maxLength = (length: number, message?: string): ValidationFn =>
  (value: any): ValidationResult => {
    if (!value) return true
    return value.length <= length ? true : 
      message || i18n.global.t('common.validation.maxLength', { length })
  }

/**
 * Validate a pattern match
 * @param pattern - The pattern to match
 * @param message - The message to display if the value does not match the pattern
 * @returns A validator function
 */
export const matches = (pattern: RegExp, message: string): ValidationFn =>
  (value: any): ValidationResult => {
    if (!value) return true
    return pattern.test(value) ? true : message
  }

/**
 * Validate a minimum value
 * @param min - The minimum value
 * @param message - The message to display if the value is less than the minimum
 * @returns A validator function
 */
export const min = (min: number, message?: string): ValidationFn =>
  (value: any): ValidationResult => {
    if (!value || Number(value) >= min) return true
    return message || i18n.global.t('common.validation.min', { min })
  }

/**
 * Validate a maximum value
 * @param max - The maximum value
 * @param message - The message to display if the value is greater than the maximum
 * @returns A validator function
 */
export const max = (max: number, message?: string): ValidationFn =>
  (value: any): ValidationResult => {
    if (!value || Number(value) <= max) return true
    return message || i18n.global.t('common.validation.max', { max })
  }

/**
 * Validate a unique email
 * @param message - The message to display if the email is not unique
 * @returns A validator function
 */
export const uniqueEmail = (message = 'Email already exists'): ValidationFn => {
  return async (value: any) => {
    if (!value) return true
    try {
      const response = await fetch(`/api/check-email?email=${value}`)
      const exists = await response.json()
      return exists ? message : true
    } catch (error: unknown) {
      return message || i18n.global.t('common.validation.email')
    }
  }
}

/**
 * Registry to store all available validators
 * @type {Record<string, ValidatorFactory>}
 */
const validatorRegistry: Record<string, ValidatorFactory> = {
  required,
  email,
  minLength,
  maxLength,
  min,
  max,
  matches,
  uniqueEmail,
}

/**
 * Parse a validation rule string into a validator function
 * @param ruleString - The validation rule string to parse
 * @returns A validator function
 * 
 * Examples: 
 * - "required"
 * - "minLength:3"
 * - "matches:^[A-Z]:Custom message"
 */

export function parseValidationRule(ruleString: string): ValidationFn {
  const [ruleName, ...params] = ruleString.split(':')
  const validator = validatorRegistry[ruleName]
  
  if (!validator) {
    fileLogger.warn(`Unknown validator: ${ruleName}`)
    return required()
  }

  // Handle parameters and custom messages
  if (params.length) {
    const parameters = params[0].split(',')
    const message = params[1] // Optional custom message

    // Convert numeric parameters
    const parsedParams = parameters.map(param => 
      /^\d+$/.test(param) ? Number(param) : param
    )

    if (message) {
      return validator(...parsedParams, message)
    }
    return validator(...parsedParams)
  }

  return validator()
}

/**
 * Create a list of validator functions from a list of validation rules
 * @param validationRules - The validation rules to create the validators from
 * @returns A list of validator functions
 */
export function createValidators(validationRules: string[]): Array<ValidationFn> {
  return validationRules.map(parseValidationRule)
}

/**
 * Register a custom validator
 * @param name - The name of the validator
 * @param validator - The validator function
 */
export function registerValidator(name: string, validator: ValidatorFactory) {
  validatorRegistry[name] = validator
}

/**
 * Validate a field against a list of validation rules
 * @param value - The value to validate
 * @param rules - The validation rules to apply
 * @returns The validation error message or null if the value is valid
 */
export const validateField = async (value: any, rules?: ValidationFn[]): Promise<string | null> => {
  if (!rules) return null
  
  for (const rule of rules) {
    const result = await rule(value)
    if (result !== true) {
      return result
    }
  }
  
  return null
}

/**
 * Validate a form with a record of field names and their validation rules
 * @param data - The data to validate
 * @param validations - A record of field names and their validation rules
 * @returns A record of field names and their validation errors
 */
export const validateForm = async (
  data: Record<string, any>,
  validations: Record<string, ValidationFn[]>
): Promise<Record<string, string>> => {
  const errors: Record<string, string> = {}
  
  for (const [field, rules] of Object.entries(validations)) {
    const error = await validateField(data[field], rules)
    if (error) {
      errors[field] = error
    }
  }
  
  return errors
}
